import { createTheme } from '@mui/material/styles';

import { ComparisonValue, HighlightedColor } from 'App/types/enums';
import { ButtonAppearance } from 'components';

export const transition = (name: string, duration = 200): string => `${name} ${duration}ms ease-in 0s`;

export const radiusSmall = '8px';

export const color = {
  transparent: 'transparent',
  orange: '#F04E39',
  lightOrange: '#F8F4E7',
  darkPurple: '#414042',
  lightRed: '#F69B8F',
  lightRed30: '#F69B8F4D',
  darkHighlight: '#AF1928',
  darkCream: '#EBE8DB',
  greyscale900: '#2D3637',
  greyscale600: '#555555',
  greyscale400: '#9D9D9D',
  greyscale200: '#D9D9D9',
  greyscale100: '#E9E9E9',
  greyscale50: '#D9D9D9',
  boxShadowColor: 'rgba(38, 38, 38, 0.1)',
  white: '#ffffff',
  black: '#000000',
  highlightTeal: '#74C8AD',
  highlightYellow: '#FDC37F',
  highlightRed: '#D84027',

  primaryLighten: '#E7EFF5',
  edgewater: '#C2DED3',
  pickledBluewood: '#2D424D',
  gothic: '#6E8B9A',
  cadetBlue: '#A8BBC5',
  catskillWhite: '#EFF5F8',
  alabaster: '#F7F7F7',
  casablanca: '#F4B24F',
  inchWorm: '#90DF0F',
  mercury: '#E9E9E9',
};

export const footerMobileHeight = '50px';
export const footerHeight = '68px';
export const headerMobileHeight = '86px';
export const headerTabletHeight = '100px';

export const theme = createTheme({
  typography: {
    fontFamily: 'Sharp Grotesk',
    body1: {
      fontFamily: 'Open Sans',
    },
    body2: {
      fontFamily: 'Open Sans',
    },
    label: {
      fontFamily: 'Open Sans',
    },
    overline: {
      fontFamily: 'Open Sans',
    },
    caption: {
      fontFamily: 'Open Sans',
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          subtitle3: 'p',
          caption: 'p',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 0,
      md: 768,
      lg: 1024,
      xl: 1265,
    },
  },
  palette: {
    common: {
      black: color.black,
      white: color.white,
    },
    primary: {
      main: color.orange,
      light: color.lightOrange,
      dark: color.darkPurple,
      contrastText: color.darkHighlight,
    },
    secondary: {
      main: color.lightRed,
      light: color.lightRed30,
    },
    grey: {
      50: color.greyscale50,
      400: color.greyscale400,
      600: color.greyscale600,
      900: color.greyscale900,
    },
    text: { primary: color.black },
    custom: {
      button: {
        [ButtonAppearance.PRIMARY]: {
          backgroundColor: color.black,
          text: color.white,
          hoverBackground: color.darkHighlight,
          activeBackground: color.darkHighlight,
          disabledBackground: color.greyscale100,
        },
        [ButtonAppearance.SECONDARY]: {
          backgroundColor: color.black,
          text: color.black,
          hoverBackground: color.darkHighlight,
          activeBackground: color.darkHighlight,
          disabledBackground: color.greyscale200,
        },
        [ButtonAppearance.LIGHT]: {
          backgroundColor: color.lightRed,
          text: color.white,
          hoverBackground: color.orange,
          activeBackground: color.orange,
          disabledBackground: color.greyscale100,
        },
      },
      comparison: {
        [ComparisonValue.DECREASE]: color.highlightTeal,
        [ComparisonValue.INCREASE]: color.darkHighlight,
        [ComparisonValue.CONSTANT]: color.highlightYellow,
      },
      boxShadow: {
        color: color.boxShadowColor,
      },
      list: {
        lightBackground: color.lightOrange,
        lightText: color.orange,
        darkText: color.darkPurple,
        lightGreyText: color.greyscale400,
        iconItemColor: color.orange,
        highlightedData: {
          [HighlightedColor.ORANGE]: color.orange,
          [HighlightedColor.BLACK]: color.black,
          [HighlightedColor.HIGHLIGHTED_RED]: color.darkHighlight,
        },
        progressBar: color.orange,
        whiteText: color.white,
      },
      circularLoading: {
        light: color.catskillWhite,
        dark: color.pickledBluewood,
      },
      input: {
        background: color.catskillWhite,
        disabledBackground: color.alabaster,
        lightText: color.gothic,
        text: color.pickledBluewood,
        disabledText: color.cadetBlue,
        error: color.casablanca,
        hoverBackground: color.edgewater,
        active: color.primaryLighten,
        readOnlyBackground: color.mercury,
        readOnlyText: color.cadetBlue,
      },
      radius: {
        radiusSmall,
      },
      layout: {
        light: color.lightOrange,
        dark: color.darkPurple,
      },
      howItWorksBackground: color.darkCream,
    },
  },

  transition,
});

theme.typography.h1 = {
  fontSize: '64px',
  lineHeight: '68px',
  fontWeight: 600,
  fontFamily: theme.typography.fontFamily,
};

theme.typography.h2 = {
  fontSize: '48px',
  lineHeight: '54px',
  fontWeight: 600,
  fontFamily: theme.typography.fontFamily,
};

theme.typography.h3 = {
  fontSize: '48px',
  lineHeight: '54px',
  fontWeight: 500,
  fontFamily: theme.typography.fontFamily,
};

theme.typography.h4 = {
  fontSize: '32px',
  lineHeight: '38px',
  fontWeight: 600,
  fontFamily: theme.typography.fontFamily,
};

theme.typography.h5 = {
  fontSize: '28px',
  lineHeight: '34px',
  fontWeight: 600,
  fontFamily: theme.typography.fontFamily,
};

theme.typography.h6 = {
  fontWeight: 500,
  fontSize: '24px',
  lineHeight: '28px',
  fontFamily: theme.typography.fontFamily,
};

theme.typography.body1 = {
  ...theme.typography.body1,
  fontSize: '16px',
  lineHeight: '22px',
  fontWeight: 400,
};

theme.typography.body2 = {
  ...theme.typography.body2,
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
};

theme.typography.subtitle1 = {
  fontSize: '18px',
  lineHeight: '24px',
  fontWeight: 500,
  fontFamily: theme.typography.fontFamily,
};

theme.typography.subtitle2 = {
  fontSize: '16px',
  lineHeight: '20px',
  fontWeight: 500,
  fontFamily: theme.typography.fontFamily,
};

theme.typography.subtitle3 = {
  fontSize: '14px',
  lineHeight: '16px',
  fontWeight: 500,
  fontFamily: theme.typography.fontFamily,
};

theme.typography.caption = {
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 400,
  fontFamily: theme.typography.fontFamily,
};

theme.typography.numbers = {
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 500,
  fontFamily: theme.typography.fontFamily,
};

theme.typography.overline = {
  fontSize: '10px',
  lineHeight: '14px',
  fontWeight: 400,
  letterSpacing: '0.02em',
  ...theme.typography.overline,
};

theme.typography.label = {
  fontSize: '10px',
  lineHeight: '14px',
  fontWeight: 400,
  letterSpacing: '0.04em',
  ...theme.typography.label,
};
